import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IoWarningOutline } from "react-icons/io5";
import { LinearProgress, Typography } from "@mui/material";
import FormAccordion from "../../../components/Form/FormAccordion";
import { FileGroup, SelectFieldStatus } from "../../../components/Form/FormComponents";
import ActionSideBar from "../partials/ActionSideBar";
import { StyledForm } from "../../../components/Form/FormComponents.styled";
import {
	ApprenticeInformation,
	AttachmentsGroup,
	FundsInformFields,
	Guarantor_1,
	Guarantor_2,
	InvestorInformation,
} from "../partials/FormFields";

// eslint-disable-next-line no-unused-vars
const OngoingForm = ({ AFdata, setAFdata, updateCurrentObject }) => {
	const [formData, setFormData] = useState(AFdata);
	const [action, setAction] = useState("view");
	const isEditable = action === "edit";
	const [ongoingProgress, setOngoingProgress] = useState(0);

	useEffect(() => {
		// Calculate the progress of the form
		let progress = 55; // Total progress 55 + 45 = 100
		if (formData?.user?.idCard) progress += 5;
		if (formData?.user?.utilityBill) progress += 5;
		if (formData?.proofOfPurchase) progress += 5;
		if (formData?.attachments) progress += 5;
		if (formData?.autoDebit) progress += 5;
		if (formData?.insurance) progress += 5;
		if (formData?.guarantor?.name1) progress += 5;
		if (formData?.guarantor?.name2) progress += 5;
		if (formData?.commitmentFee) progress += 5;

		setOngoingProgress(progress);
	}, []);

	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleCancel = () => {
		setFormData(AFdata);
		setAction("view");
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		console.log(formData);
		setAFdata(formData);
		updateCurrentObject(formData);
	};

	return (
		<>
			<StyledForm onSubmit={handleSubmit}>
				<ActionSideBar AFdata={formData} action={action} setAction={setAction} handleCancel={handleCancel} />

				<div className='RightSection'>
					<div className='headerActions'>
						<div className='progress'>
							<Typography variant='p' component='p' pb={1}>
								This deal is {ongoingProgress}% complete.
							</Typography>
							<LinearProgress
								variant='determinate'
								color='success'
								value={ongoingProgress}
								sx={{ height: "6px", borderRadius: "8px" }}
							/>
						</div>

						{ongoingProgress < 100 && (
							<Typography variant='p' color='error' className='flex' sx={{ fontWeight: 500 }}>
								<IoWarningOutline style={{ marginRight: "10px", fontSize: "18px" }} />
								Details marked red are not yet completed.
							</Typography>
						)}
					</div>

					<FormAccordion title='Funds Information' open={true}>
						<div className='grid grid-cols-3 gap-3'>
							<FundsInformFields
								formData={formData}
								isEditable={isEditable}
								handleInputChange={handleInputChange}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='ID card' open={formData?.user?.idCard ? false : true}>
						{!formData?.user?.idCard ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='form-group'>
								<FileGroup name='idCard' value={formData?.user?.idCard} />
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Utility bill' open={formData?.user?.utilityBill ? false : true}>
						{!formData?.user?.utilityBill ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='form-group'>
								<FileGroup name='utilityBill' value={formData?.user?.utilityBill} />
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Proof of purchase' open={formData?.proofOfPurchaseFile ? false : true}>
						{!formData?.proofOfPurchaseFile ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='form-group'>
								<FileGroup value={formData?.proofOfPurchaseFile?.url} />
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Attachments' open={formData?.attachmentDoc ? false : true}>
						{!formData?.attachmentDoc ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<AttachmentsGroup formData={formData} />
						)}
					</FormAccordion>

					<FormAccordion title='Apprentice’s Information'>
						<ApprenticeInformation formData={formData} />
					</FormAccordion>

					<FormAccordion title='Investor’s Information'>
						<InvestorInformation formData={formData} />
					</FormAccordion>

					<FormAccordion title='Auto debit' open={formData?.autoDebit ? false : true}>
						{!formData?.autoDebit ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus
										name='autoDebit'
										value={formData?.autoDebit ? "Enabled" : "Disabled"}
										type={formData?.autoDebit ? "success" : "error"}
									/>
								</div>
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Insurance' open={formData?.insurance ? false : true}>
						{!formData?.insurance ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus
										name='insurance'
										value={formData?.insurance ? "Enabled" : "Disabled"}
										type={formData?.insurance ? "success" : "error"}
									/>
								</div>
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Guarantor 1' open={formData?.guarantor?.name1 ? false : true}>
						{!formData?.guarantor?.name1 ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<Guarantor_1 formData={formData} />
						)}
					</FormAccordion>

					<FormAccordion title='Guarantor 2' open={formData?.guarantor?.name2 ? false : true}>
						{!formData?.guarantor?.name2 ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<Guarantor_2 formData={formData} />
						)}
					</FormAccordion>

					<FormAccordion title='Commitment fee' open={formData?.commitmentFee ? false : true}>
						{!formData?.commitmentFee ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus
										name='commitmentFee'
										// handleChange={handleInputChange}
										// isEditable={isEditable}
										value={"₦" + formData?.commitmentFee}
										type={"success"}
									/>
								</div>
							</div>
						)}
					</FormAccordion>
				</div>
			</StyledForm>
		</>
	);
};

// Props Validation
OngoingForm.propTypes = {
	AFdata: PropTypes.object,
	setAFdata: PropTypes.func,
	updateCurrentObject: PropTypes.func,
};

export default OngoingForm;
